import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, Text, Truncated, useTooltip } from '@frontend/design-system';
import type { Workspace } from '../../types';

type WorkspaceItemProps = {
  workspace: Workspace;
  accessibleCount: number;
  totalLocationCount: number;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
};

export function WorkspaceItem({
  workspace,
  accessibleCount,
  totalLocationCount,
  isSelected,
  onClick,
  disabled = false,
}: WorkspaceItemProps) {
  const { t } = useTranslation('settings');

  const missingCount = totalLocationCount - accessibleCount;

  const tooltipLabel = useMemo(() => {
    if (!disabled) return undefined;
    return t(
      'You are not assigned to {{count}} out of {{total}} locations in this workspace, please contact your admin.',
      { count: missingCount, total: totalLocationCount }
    );
  }, [disabled, missingCount, totalLocationCount, t]);

  const tooltip = useTooltip({
    placement: 'top',
  });

  return (
    <div {...tooltip.triggerProps}>
      <NakedButton
        onClick={disabled ? undefined : onClick}
        css={css`
          display: flex;
          align-items: center;
          gap: ${theme.spacing(1.25)};
          min-height: 72px;
          padding: ${theme.spacing(1.5, 2)};
          border-radius: ${theme.borderRadius.medium};
          border: 1px solid ${isSelected ? theme.colors.primary50 : theme.colors.neutral20};
          width: 100%;
          text-align: left;
          background: ${isSelected ? theme.colors.primary5 : theme.colors.white};
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          opacity: ${disabled ? 0.5 : 1};
          transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

          &:hover {
            ${!disabled &&
            `
            border-color: ${isSelected ? theme.colors.primary50 : theme.colors.neutral40};
            box-shadow: 0 1px 4px ${theme.colors.neutral20};
            `}
          }

          &:focus-visible {
            outline: 2px solid ${theme.colors.primary};
            outline-offset: 1px;
          }
        `}
      >
        <Icon name='bookmark-small' size={18} color={isSelected ? 'primary' : 'light'} />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(0.125)};
            flex: 1;
            min-width: 0;
          `}
        >
          <Truncated
            as={Text}
            size='medium'
            weight='semibold'
            color='default'
            tooltipOptions={{ placement: 'top-start' }}
            css={css`
              max-width: 180px;
            `}
          >
            {workspace.workspaceName}
          </Truncated>
          <Text size='small' color='light'>
            {t('{{count}} locations', { count: totalLocationCount })}
          </Text>
        </div>
      </NakedButton>
      {disabled && tooltipLabel && tooltip.isOpen && (
        <tooltip.Tooltip {...tooltip.tooltipProps}>
          <Text size='small' color='white'>
            {tooltipLabel}
          </Text>
        </tooltip.Tooltip>
      )}
    </div>
  );
}
